<script lang="ts" setup>import { computed as _computed } from 'vue';

import { Heading } from '@pohlcon/design-system';
import { useI18n } from 'vue-i18n';
import { ISOPROProductHeightOffsetVariantType, type Attribute, type ISOPROProduct, type ISOPROProductBendingMomentType, HeightOffsetVariantType, ISOPROProductSMType, ISOPROProductSMVariantType, ISOPROProductShearForceType, ISOPROProductShortElementType, IBendingMomentProperties, IProductProperties } from '~/models';
import PrintAttribute from './PrintAttribute.vue';

const { t } = useI18n();

interface Props {
  product: ISOPROProduct;
}

const props = defineProps<Props>();


const attributes: Record<string, Attribute> = _computed(() => {
  const { rowKey, properties: { dimensions, construction }, height, fireProtectionType }: ISOPROProduct & {
    properties: {
      construction: {
        tensionRods: {
          number: number;
          diameter: number;
        };
        thrustBearings: {
          numberOfThrustShearNegBearing?: number;
          diameterOfThrustBearing?: number;
          diameterOfThrustShearNegBearings?: number;
          diameterOfThrustShearPosBearings?: number;
          numberOfThrustShearPosBearing?: number;
        };
      },
    };
  } = props.product as ISOPROProduct &
  {
    properties: {
      construction: {
        tensionRods: {
          number: number; diameter: number;
        };
        thrustBearings: {
          numberOfThrustShearNegBearing?: number;
          diameterOfThrustBearing?: number;
          diameterOfThrustShearNegBearings?: number;
          diameterOfThrustShearPosBearings?: number;
          numberOfThrustShearPosBearing?: number;
        };
      };
    };
  };
  const minimumWallThickness = props.product instanceof ISOPROProductHeightOffsetVariantType
    ? props.product.minimumWallThickness : null;

  const minimumCeilingThickness =
    props.product instanceof ISOPROProductHeightOffsetVariantType
      && props.product.variant === 'UV'
      && !!props.product.minimumCeilingThickness
      ? props.product.minimumCeilingThickness
      : null;

  const variant = props.product instanceof ISOPROProductHeightOffsetVariantType || props.product instanceof ISOPROProductSMVariantType
    ? props.product.variant
    : null;

  const adaptedKindOfThrustBearing = construction.thrustBearings.kindOfThrustBearing?.replaceAll(/\d+.\d+$/g, '').split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('');

  return {
    product: {
      title: 'product',
      value: t('printView.preview.attributes.product.value'),
      toPrint: true
    },
    code: {
      title: 'code',
      value: rowKey,
      toPrint: true
    },
    height: {
      title: 'height',
      value: height.toFixed(0) + ' mm',
      toPrint: true
    },
    length: {
      title: 'length',
      value: dimensions.length.toFixed(0) + ' mm',
      toPrint: true
    },
    fireProtectionType: {
      title: 'fireProtectionType',
      value: fireProtectionType === "R0" ? t('printView.preview.attributes.fireProtectionType.noFireProtection') : fireProtectionType,
      toPrint: true
    },
    insulationThickness: {
      title: 'insulationThickness',
      value: dimensions.insulationThickness.toFixed(0) + ' mm',
      toPrint: true
    },
    concreteCovering: {
      title: 'concreteCovering',
      value: dimensions.concreteCovering.toFixed(0) + ' mm',
      toPrint: true
    },
    numberOfThrustBearings: {
      title: 'numberOfThrustBearings',
      value: construction.thrustBearings.numberOfThrustBearings?.toFixed(0) || null,
      toPrint: true
    },
    diameterOfThrustBearing: {
      title: 'diameterOfThrustBearing',
      value: construction.thrustBearings.diameterOfThrustBearing
        ? construction.thrustBearings.diameterOfThrustBearing.toFixed(0) + ' mm'
        : null,
      toPrint: true
    },
    kindOfThrustBearing: {
      title: 'kindOfThrustBearing',
      value: construction.thrustBearings.kindOfThrustBearing ? t(`printView.preview.attributes.kindOfThrustBearing.values.${adaptedKindOfThrustBearing}`) : null,
      toPrint: true
    },
    numberOfThrustShearNegBearing: {
      title: 'numberOfThrustShearNegBearing',
      value: construction.thrustBearings.numberOfThrustShearNegBearing?.toFixed(0) || '',
      toPrint: true
    },
    diameterOfThrustShearNegBearings: {
      title: 'diameterOfThrustShearNegBearings',
      value: construction.thrustBearings.diameterOfThrustShearNegBearings
        ? construction.thrustBearings.diameterOfThrustShearNegBearings.toFixed(0) + ' mm'
        : '',
      toPrint: true
    },
    numberOfThrustShearPosBearing: {
      title: 'numberOfThrustShearPosBearing',
      value: construction.thrustBearings.numberOfThrustShearPosBearing?.toFixed(0) || '',
      toPrint: true
    },
    diameterOfThrustShearPosBearings: {
      title: 'diameterOfThrustShearPosBearings',
      value: construction.thrustBearings.diameterOfThrustShearPosBearings
        ? construction.thrustBearings.diameterOfThrustShearPosBearings.toFixed(0) + ' mm'
        : '',
      toPrint: true
    },
    numberOfTensionRods: {
      title: 'numberOfTensionRods',
      value: construction.tensionRods.number ? construction.tensionRods.number.toFixed(0) : null,
      toPrint: true
    },
    diameterOfTensionRods: {
      title: 'diameterOfTensionRods',
      value: construction.tensionRods.diameter ? construction.tensionRods.diameter.toFixed(0) + ' mm' : null,
      toPrint: true
    },
    variant: {
      title: 'variant',
      value: variant,
      toPrint: true
    },
    minimumWallThickness: {
      title: 'minWallThickness',
      value: minimumWallThickness ? (+minimumWallThickness).toFixed(0) !== 'NaN' ? (+minimumWallThickness).toFixed(0) + ' mm' : minimumWallThickness.toString() : null,
      toPrint: true
    },
    minimumCeilingThickness: {
      title: 'minCeilThickness',
      value: minimumCeilingThickness ? minimumCeilingThickness + ' mm' : null,
      toPrint: true
    }
  };
});

const descriptionImg: Record<'imgURL', string> | null = _computed(() => {
  const type = `${props.product.type}`;
  if (type === 'M') {
    if (props.product instanceof ISOPROProductHeightOffsetVariantType) {
      return null;
      // { imgURL: `./assets/productConstruction/${props.product.variant}.svg`; };
    }
    const isDoubleShearForce = (props.product as ISOPROProductBendingMomentType).shearForceLoadBearingCapacityCode.split("Q").length > 2;
    return isDoubleShearForce
      ? { imgURL: `./assets/productConstruction/${type}QQ.svg` }
      : { imgURL: `./assets/productConstruction/${type}Q.svg` };
  } else if (props.product instanceof ISOPROProductSMType || props.product instanceof ISOPROProductSMVariantType) {
    if (props.product.shearForceLoadBearingCapacityCode === 'QQ') return { imgURL: `./assets/productConstruction/${type}QQ.svg` }
    else {
      return { imgURL: `./assets/productConstruction/${type}${props.product.capacity}.svg` };
    }
  } else if (type === 'SQ') {
    return { imgURL: `./assets/productConstruction/${type}.svg` }
  } else {
    return {
      imgURL: `./assets/productConstruction/${type.replace('S', '')}.svg`
    };
  }
});
</script>

<template>
  <article class="product-description">
    <div>
      <PrintAttribute :attribute="attributes.product" />
      <PrintAttribute :attribute="attributes.code" />
      <PrintAttribute v-if="attributes.variant.value" :attribute="attributes.variant" />
      <PrintAttribute :attribute="attributes.fireProtectionType" />
      <Heading :level="4">{{ t('printView.preview.attributeCategories.dimensions') }}</Heading>
      <PrintAttribute :attribute="attributes.height" />
      <PrintAttribute :attribute="attributes.length" />
      <PrintAttribute :attribute="attributes.insulationThickness" />
      <PrintAttribute :attribute="attributes.concreteCovering" />
      <Heading :level="4">{{ t('printView.preview.attributeCategories.construction') }}</Heading>
      <PrintAttribute :attribute="attributes.numberOfThrustBearings" />
      <PrintAttribute v-if="attributes.diameterOfThrustBearing.value" :attribute="attributes.diameterOfThrustBearing" />
      <PrintAttribute v-if="attributes.kindOfThrustBearing.value" :attribute="attributes.kindOfThrustBearing" />
      <PrintAttribute v-if="attributes.numberOfThrustShearPosBearing.value"
        :attribute="attributes.numberOfThrustShearPosBearing" />
      <PrintAttribute v-if="attributes.diameterOfThrustShearPosBearings.value" :attribute="attributes.diameterOfThrustShearPosBearings"/>
      <PrintAttribute v-if="attributes.numberOfThrustShearNegBearing.value"
        :attribute="attributes.numberOfThrustShearNegBearing" />
      <PrintAttribute v-if="attributes.diameterOfThrustShearNegBearings.value" :attribute="attributes.diameterOfThrustShearNegBearings"/>
      <PrintAttribute v-if="attributes.numberOfTensionRods.value" :attribute="attributes.numberOfTensionRods" />
      <PrintAttribute v-if="attributes.diameterOfTensionRods.value" :attribute="attributes.diameterOfTensionRods" />
      <PrintAttribute v-if="attributes.minimumWallThickness.value" :attribute="attributes.minimumWallThickness" />
      <PrintAttribute v-if="attributes.minimumCeilingThickness.value" :attribute="attributes.minimumCeilingThickness" />
    </div>
    <div class="images">
      <img v-if="!(attributes.variant.value && product.type !== 'SM')" :src="product.imageURL" class="images__product" alt="product image" loading="lazy" />
      <img v-if="descriptionImg" :src="descriptionImg.imgURL" alt="product's constructional composition" loading="lazy" />
    </div>
  </article>
</template>

<style lang="scss">
@use '../../_variables' as *;


.product-description {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: var(--gutter-xl);
  justify-content: space-between;
  border-bottom: 1px solid var(--bds-primaryText-color);
  margin-bottom: var(--gutter-l);

  .heading {
    margin-bottom: var(--gutter-m) !important;
  }

  label.label[labelPosition='labelLeading'] {
    // grid-template-columns: 1fr auto;
  }

  .images {
    display: grid;
    grid-gap: 100px;
  }

  img {
    max-width: 200px;
    object-fit: contain;
    mix-blend-mode: darken;

    @media print {
      max-width: 80mm;
    }
  }

  @media screen and (max-width: $breakpoint-tablet) {
    grid-auto-flow: row;

    .images {
      grid-gap: 50px;
      margin-bottom: var(--gutter-l);
    }
  }
}
</style>
